import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSignIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function BudgetVsActualReport({
  filters,
  locale
}) {
  // Sample data for budget vs actual report
  const budgetData = [{
    department: 'Marketing',
    budget: 150000,
    actual: 142500,
    variance: -7500
  }, {
    department: 'Sales',
    budget: 200000,
    actual: 215000,
    variance: 15000
  }, {
    department: 'IT',
    budget: 300000,
    actual: 285000,
    variance: -15000
  }, {
    department: 'Operations',
    budget: 250000,
    actual: 248000,
    variance: -2000
  }, {
    department: 'HR',
    budget: 100000,
    actual: 98500,
    variance: -1500
  }, {
    department: 'Finance',
    budget: 120000,
    actual: 115000,
    variance: -5000
  }];
  const monthlyData = [{
    month: 'Jan',
    budget: 95000,
    actual: 92000
  }, {
    month: 'Feb',
    budget: 100000,
    actual: 98500
  }, {
    month: 'Mar',
    budget: 110000,
    actual: 115000
  }, {
    month: 'Apr',
    budget: 105000,
    actual: 107000
  }, {
    month: 'May',
    budget: 115000,
    actual: 110000
  }, {
    month: 'Jun',
    budget: 120000,
    actual: 118000
  }];
  const totalBudget = budgetData.reduce((sum, item) => sum + item.budget, 0);
  const totalActual = budgetData.reduce((sum, item) => sum + item.actual, 0);
  const totalVariance = totalActual - totalBudget;
  const variancePercentage = totalVariance / totalBudget * 100;
  return <div className="p-6" data-id="element-16041">
      <div className="mb-8" data-id="element-16042">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-16043">
          Budget vs Actual Spend Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16044">
          Compare budgeted amounts against actual spend by department, GL code,
          or entity
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-16045">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16046">
          <div className="p-5" data-id="element-16047">
            <div className="flex items-center" data-id="element-16048">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-16049">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-16050" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16051">
                <dl data-id="element-16052">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16053">
                    Total Budget
                  </dt>
                  <dd data-id="element-16054">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16055">
                      {formatCurrency(totalBudget, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16056">
          <div className="p-5" data-id="element-16057">
            <div className="flex items-center" data-id="element-16058">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-16059">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-16060" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16061">
                <dl data-id="element-16062">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16063">
                    Total Actual
                  </dt>
                  <dd data-id="element-16064">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16065">
                      {formatCurrency(totalActual, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16066">
          <div className="p-5" data-id="element-16067">
            <div className="flex items-center" data-id="element-16068">
              <div className={`flex-shrink-0 ${totalVariance < 0 ? 'bg-green-500' : 'bg-red-500'} rounded-md p-3`} data-id="element-16069">
                {totalVariance < 0 ? <TrendingDownIcon className="h-6 w-6 text-white" data-id="element-16070" /> : <TrendingUpIcon className="h-6 w-6 text-white" data-id="element-16071" />}
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16072">
                <dl data-id="element-16073">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16074">
                    Variance
                  </dt>
                  <dd data-id="element-16075">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16076">
                      {formatCurrency(totalVariance, 'USD', locale)} (
                      {variancePercentage.toFixed(1)}%)
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Department Chart */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-16077">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16078">
          Budget vs Actual by Department
        </h4>
        <div className="h-80" data-id="element-16079">
          <ResponsiveContainer width="100%" height="100%" data-id="element-16080">
            <BarChart data={budgetData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-16081">
              <CartesianGrid strokeDasharray="3 3" data-id="element-16082" />
              <XAxis dataKey="department" data-id="element-16083" />
              <YAxis data-id="element-16084" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-16085" />
              <Legend data-id="element-16086" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-16087" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-16088" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Monthly Trend */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-16089">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16090">
          Monthly Budget vs Actual Trend
        </h4>
        <div className="h-80" data-id="element-16091">
          <ResponsiveContainer width="100%" height="100%" data-id="element-16092">
            <BarChart data={monthlyData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-16093">
              <CartesianGrid strokeDasharray="3 3" data-id="element-16094" />
              <XAxis dataKey="month" data-id="element-16095" />
              <YAxis data-id="element-16096" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-16097" />
              <Legend data-id="element-16098" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-16099" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-16100" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Department Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-16101">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-16102">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16103">
            Department Budget Details
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16104">
            Detailed breakdown by department
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-16105">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-16106">
            <thead className="bg-gray-50" data-id="element-16107">
              <tr data-id="element-16108">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16109">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16110">
                  Budget
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16111">
                  Actual
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16112">
                  Variance
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16113">
                  % Variance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-16114">
              {budgetData.map((dept, index) => <tr key={index} data-id="element-16115">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-16116">
                    {dept.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-16117">
                    {formatCurrency(dept.budget, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-16118">
                    {formatCurrency(dept.actual, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-16119">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-16120">
                      {formatCurrency(dept.variance, 'USD', locale)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-16121">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-16122">
                      {(dept.variance / dept.budget * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}